import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { createSearchParams, useHref, useSearchParams } from "react-router-dom";

import { CollectionHeading } from "../components/CollectionHeading";
import { CollectionSkeleton } from "../components/CollectionSkeleton";
import { SheetsReconnectBanner } from "../components/SheetsReconnectBanner";
import { FilterChips } from "../components/FilterChips";
import { getApi } from "./get-api";
import {
  drawerTargetFromParams,
  drawerTargetKey,
  withDrawerTarget,
} from "./drawer-url";
import { phoneListFromParams, phoneListToParams } from "./phone-list-params";
import { applyParamPatch, readLiveSearchParams } from "./url-params";
import { Field, humanize, withObservedEnums } from "./schema-field";
import { useAuth } from "./useAuth";
import { useDynamicDataTable } from "./useDynamicDataTable";
import { useDynamicForm } from "./useDynamicForm";
import { useSyncHeader } from "./useSyncHeader";

type FilterGroupCondition = {
  field: string;
  operator: string;
  value: string | string[] | boolean;
};

interface UseDataManagementProps {
  datasourceId: string;
  collectionName: string;
  schema: Field[];
  /** Demo datasource: show write chrome, but simulate every write. */
  demoMode?: boolean;
  /** Non-demo read-only datasource: hide all write chrome. */
  readOnly?: boolean;
  /** Google Sheets datasource: poll the sync state for the heading (ARD 9.6). */
  sheetsSync?: boolean;
  /**
   * Overview table cards already print the collection name on DashboardCard.
   * Skip the inner h1 so the card names the table once; keep count / demo /
   * New record.
   */
  embedded?: boolean;
  /**
   * Collection.tsx draws the page h1 above the chart row so chart card h2s
   * stay after it. Skip the inner heading when the page owns it.
   */
  hideHeading?: boolean;
  /** After a successful form save (grid already refetches). Phone search uses this. */
  onSaved?: () => void;
  /**
   * Follow `open=` / `new=` on this hook. False until the datasource is known,
   * and false for Overview cards, so a not-yet-loaded demo does not strip the
   * link and a card does not fetch another table's id.
   */
  rowLinks?: boolean;
  /**
   * Route of the members page, for the Share button in the heading. Set only
   * for the owner of a real panel (MOSA-367 slice 1).
   */
  shareHref?: string;
  /**
   * Route of the Activity list, for the Activity button in the heading. Set
   * only for the owner of a real panel (MOSA-368 slice 1).
   */
  activityHref?: string;
}

export const useDataManagement = ({
  datasourceId,
  collectionName,
  schema,
  demoMode,
  readOnly,
  sheetsSync = false,
  embedded = false,
  hideHeading = false,
  onSaved,
  rowLinks = false,
  shareHref,
  activityHref,
}: UseDataManagementProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const usesHashRouting = useHref("/").includes("#");
  const { token } = useAuth();
  const api = useMemo(() => getApi({ token }), [token]);

  const filterGroups =
    searchParams.get("filterGroups") || searchParams.get("filters") || undefined;
  const sort = searchParams.get("sort") || undefined;
  const phoneList = phoneListFromParams(searchParams);

  // `setSearchParams` is a fresh function on every URL change. Keeping it in a
  // ref makes `handleFilterChange` stable, so the filter bar is not re-notified
  // every time an unrelated query param moves.
  const setSearchParamsRef = useRef(setSearchParams);
  useLayoutEffect(() => {
    let currentParams = searchParams;
    setSearchParamsRef.current = (nextInit, options) => {
      const current = usesHashRouting
        ? readLiveSearchParams({ hash: window.location.hash, search: "" })
        : currentParams;
      currentParams = createSearchParams(
        typeof nextInit === "function" ? nextInit(current) : nextInit
      );
      setSearchParams(currentParams, options);
    };
  }, [searchParams, setSearchParams, usesHashRouting]);

  /**
   * Which drawer URL we have already applied, including "cleared". Stops a
   * click that just wrote `open=` from fetching the same row again.
   */
  const openedDrawerRef = useRef<string | null>(null);
  const canOpenLinkedRow = !!demoMode || !readOnly;
  const syncDrawerUrl = useCallback(
    (target: { kind: "edit"; id: string } | { kind: "create" } | null) => {
      if (!rowLinks) return;
      if (target?.kind === "edit" && !canOpenLinkedRow) return;
      if (target?.kind === "create" && readOnly) return;
      openedDrawerRef.current = drawerTargetKey(collectionName, target);
      setSearchParamsRef.current(
        (prev) => {
          const current = new URLSearchParams(prev);
          const next = withDrawerTarget(current, target);
          if (next.toString() === current.toString()) return prev;
          return next;
        },
        { replace: true }
      );
    },
    [rowLinks, canOpenLinkedRow, readOnly, collectionName]
  );

  const handleFilterChange = useCallback(
    (filterGroupsJson: string | undefined) => {
      setSearchParamsRef.current(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (filterGroupsJson) next.set("filterGroups", filterGroupsJson);
          else next.delete("filterGroups");
          next.delete("filters"); // legacy param
          return next;
        },
        { replace: true }
      );
    },
    []
  );

  const handleSortChange = useCallback((next: string | undefined) => {
    setSearchParamsRef.current(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set("sort", next);
        else params.delete("sort");
        return params;
      },
      { replace: true }
    );
  }, []);

  /**
   * Phone search writes the same keys the grid does. `open` / `sort` /
   * `filterGroups` stay; page 1 and the phone's default size are omitted.
   */
  const handleListParamsChange = useCallback(
    (next: { q: string; page: number; pageSize: number }) => {
      setSearchParamsRef.current(
        (prev) => applyParamPatch(new URLSearchParams(prev), phoneListToParams(next)),
        { replace: true }
      );
    },
    []
  );

  const clearFilters = useCallback(
    () => handleFilterChange(undefined),
    [handleFilterChange]
  );

  /**
   * The grid's right-click "filter by this value". One condition per
   * field/operator pair, so right-clicking two cells in the same column with
   * the same operator replaces the condition instead of stacking a
   * contradiction that matches nothing.
   */
  const addFilter = useCallback(
    (field: string, operator: string, value: string | boolean) => {
      let parsed: { operator?: "AND" | "OR"; filters?: FilterGroupCondition[] } =
        {};
      try {
        parsed = filterGroups ? JSON.parse(filterGroups) : {};
      } catch {
        parsed = {};
      }
      const existing = (parsed.filters ?? []).filter(
        (f) => !(f.field === field && f.operator === operator)
      );
      handleFilterChange(
        JSON.stringify({
          operator: parsed.operator ?? "AND",
          filters: [...existing, { field, operator, value }],
        })
      );
    },
    [filterGroups, handleFilterChange]
  );

  const filtersNode = useMemo(
    () =>
      schema && schema.length > 0 ? (
        <FilterChips
          schema={schema}
          onFilterChange={handleFilterChange}
          initialFilters={filterGroups}
        />
      ) : null,
    [schema, handleFilterChange, filterGroups]
  );

  const { sync } = useSyncHeader({
    datasourceId,
    enabled: sheetsSync,
  });

  const {
    Toolbar,
    DataTable,
    data,
    loading,
    fetching,
    error,
    reconnect,
    total,
    fetchData,
    paginationModel,
    onPaginationChange,
  } = useDynamicDataTable({
      datasourceId,
      collection: collectionName,
      schema,
      idField: "id",
      filterGroups,
      demoMode,
      readOnly,
      filtersNode,
      hasActiveFilters: !!filterGroups,
      onClearFilters: clearFilters,
      onAddFilter: addFilter,
      onDrawerTarget: rowLinks ? syncDrawerUrl : undefined,
      hideToolbar: embedded,
      hidePagination: embedded,
    });

  const formSchema = useMemo(
    () => withObservedEnums(schema, data),
    [schema, data]
  );

  const { FormModal, openForm } = useDynamicForm({
    title: collectionName,
    datasourceId,
    collection: collectionName,
    demoMode,
    pageRows: data,
    rowCount: total,
    paginationModel,
    onPageChange: (page) =>
      onPaginationChange({ ...paginationModel, page }),
    pageLoading: loading,
    onSubmit: () => {
      fetchData();
      onSaved?.();
    },
    schema: formSchema,
    onDrawerTarget: rowLinks ? syncDrawerUrl : undefined,
    canDelete: !readOnly,
  });

  const openFormRef = useRef(openForm);
  useEffect(() => {
    openFormRef.current = openForm;
  }, [openForm]);

  /**
   * `open=<pk>` and `new=1` stay in the URL while the drawer is open, so a
   * refresh or a copied link reopens the same record. Related-record hops
   * already write those keys; this effect only opens, it does not strip them.
   * A viewer still cannot open a drawer, so those keys are cleared once.
   */
  useEffect(() => {
    if (!rowLinks) return;

    const target = drawerTargetFromParams(searchParams);
    const key = drawerTargetKey(collectionName, target);
    if (!target) {
      openedDrawerRef.current = null;
      return;
    }
    if (openedDrawerRef.current === key) return;

    const stripDrawer = () => {
      openedDrawerRef.current = null;
      setSearchParamsRef.current(
        (prev) => withDrawerTarget(new URLSearchParams(prev), null),
        { replace: true }
      );
    };

    if (target.kind === "edit" && !canOpenLinkedRow) {
      openedDrawerRef.current = key;
      stripDrawer();
      return;
    }
    if (target.kind === "create") {
      if (readOnly) {
        openedDrawerRef.current = key;
        stripDrawer();
        return;
      }
      let parsed: Record<string, unknown> = {};
      const defaultsRaw = searchParams.get("defaults");
      if (defaultsRaw) {
        try {
          const value = JSON.parse(defaultsRaw);
          if (value && typeof value === "object" && !Array.isArray(value)) {
            parsed = value as Record<string, unknown>;
          }
        } catch {
          parsed = {};
        }
      }
      openedDrawerRef.current = key;
      openFormRef.current(parsed, { isUpdate: false, fromUrl: true });
      return;
    }

    if (!token) return;
    openedDrawerRef.current = key;
    let cancelled = false;
    (async () => {
      let row: Record<string, unknown> | null = null;
      try {
        const response = (await api.data.getData(
          datasourceId,
          collectionName,
          String(target.id)
        )) as { data?: unknown };
        const body = response?.data;
        if (body && typeof body === "object" && !Array.isArray(body)) {
          row = body as Record<string, unknown>;
        }
      } catch {
        row = null;
      }
      if (cancelled || openedDrawerRef.current !== key) return;
      if (row) {
        openFormRef.current(row, { isUpdate: true, fromUrl: true });
        return;
      }
      stripDrawer();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    readOnly,
    canOpenLinkedRow,
    rowLinks,
    token,
    api,
    datasourceId,
    collectionName,
  ]);

  const title = humanize(collectionName);
  const showTableSkeleton = (loading && !total) || !schema?.length;

  const Manager = useMemo(
    () => (
      <div
        className={`flex min-h-0 min-w-0 flex-col gap-3${
          embedded ? " px-4 pt-3" : ""
        }`}
      >
        {!embedded && !hideHeading && (
          <CollectionHeading
            title={title}
            total={total}
            loading={loading}
            demoMode={!!demoMode}
            sync={sync}
            onCreate={!readOnly ? () => openForm() : undefined}
            shareHref={shareHref}
            activityHref={activityHref}
          />
        )}

        <div
          data-od-table-pane
          className="flex min-h-0 min-w-0 flex-col overflow-hidden"
        >
          {/* Embedded, the DashboardCard already draws
              `rounded-xl border border-od-border bg-od-surface`; repeating it
              here painted two concentric borders in the same colour. Standalone
              there is no outer card, so the pane keeps its own frame. */}
          <div
            className={`flex min-h-0 min-w-0 w-full flex-col overflow-hidden ${
              embedded
                ? ""
                : "rounded-xl border border-od-border bg-od-surface "
            }${showTableSkeleton ? "flex-1" : "h-min max-h-full"}`}
          >
            {showTableSkeleton ? (
              <CollectionSkeleton variant="inset" />
            ) : (
              <>
                {Toolbar}
                <div className="min-h-0 min-w-0 grow">{DataTable}</div>
              </>
            )}
          </div>
        </div>

        {(demoMode || !readOnly) && FormModal}

        {reconnect ? (
          <SheetsReconnectBanner info={reconnect} />
        ) : error ? (
          <div
            role="alert"
            className="rounded-lg border px-3 py-2 text-xs"
            style={{
              color: "var(--od-danger)",
              borderColor: "color-mix(in srgb, var(--od-danger) 40%, transparent)",
              backgroundColor:
                "color-mix(in srgb, var(--od-danger) 10%, transparent)",
            }}
          >
            {error}
          </div>
        ) : null}
      </div>
    ),
    [
      Toolbar,
      DataTable,
      FormModal,
      error,
      reconnect,
      title,
      total,
      loading,
      showTableSkeleton,
      demoMode,
      readOnly,
      sync,
      embedded,
      hideHeading,
      openForm,
      shareHref,
      activityHref,
    ]
  );

  return {
    Manager,
    FormModal,
    openForm,
    error,
    reconnect,
    loading,
    fetching,
    total,
    data,
    fetchData,
    title,
    filterGroups,
    onFilterChange: handleFilterChange,
    sort,
    onSortChange: handleSortChange,
    searchQuery: phoneList.q,
    listPage: phoneList.page,
    listPageSize: phoneList.pageSize,
    onListParamsChange: handleListParamsChange,
    sync,
  };
};
